import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Forbidden } from "@/components/shared/Common";
import { api } from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import Install from "@/pages/Install";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordRecovery";
import Dashboard from "@/pages/Dashboard";
import MyAccount from "@/pages/MyAccount";
import Notifications from "@/pages/Notifications";
import Architecture from "@/pages/Architecture";
import Users from "@/pages/admin/Users";
import UserDetail from "@/pages/admin/UserDetail";
import Roles from "@/pages/admin/Roles";
import Audit from "@/pages/admin/Audit";
import Sessions from "@/pages/admin/Sessions";
import Settings from "@/pages/admin/Settings";
import Catalogs from "@/pages/admin/Catalogs";
import Backups from "@/pages/admin/Backups";
import Messages from "@/pages/comunicacion/Messages";
import Tickets from "@/pages/comunicacion/Tickets";
import TicketDetail from "@/pages/comunicacion/TicketDetail";
import { TerrenoRoutes } from "@/pages/terreno/TerrenoRoutes";
import GerenciaDashboard from "@/pages/gerencia/GerenciaDashboard";
import Clients from "@/pages/gerencia/Clients";
import ClientDetail from "@/pages/gerencia/ClientDetail";
import Sales from "@/pages/gerencia/Sales";
import SaleDetail from "@/pages/gerencia/SaleDetail";
import Ots from "@/pages/gerencia/Ots";
import OtForm from "@/pages/gerencia/OtForm";
import OtDetail from "@/pages/gerencia/OtDetail";
import CostCenters from "@/pages/gerencia/CostCenters";
import Employees from "@/pages/rrhh/Employees";
import EmployeeDetail from "@/pages/rrhh/EmployeeDetail";
import DocTypes from "@/pages/rrhh/DocTypes";
import ControlDoc from "@/pages/rrhh/ControlDoc";
import Contracts from "@/pages/rrhh/Contracts";
import ContractTemplates from "@/pages/rrhh/ContractTemplates";
import Attendance from "@/pages/rrhh/Attendance";
import Payroll from "@/pages/rrhh/Payroll";
import PayrollDetail from "@/pages/rrhh/PayrollDetail";
import Scan from "@/pages/rrhh/Scan";
import AbDashboard from "@/pages/abastecimiento/AbDashboard";
import Warehouses from "@/pages/abastecimiento/Warehouses";
import WarehouseDetail from "@/pages/abastecimiento/WarehouseDetail";
import Products from "@/pages/abastecimiento/Products";
import Transfers from "@/pages/abastecimiento/Transfers";
import Movements from "@/pages/abastecimiento/Movements";
import Tools from "@/pages/abastecimiento/Tools";
import ToolDesk from "@/pages/abastecimiento/ToolDesk";
import CountDetail, { Counts } from "@/pages/abastecimiento/Counts";
import OpsBoard from "@/pages/operaciones/OpsBoard";
import OpsOt from "@/pages/operaciones/OpsOt";
import DayBoard from "@/pages/rrhh/DayBoard";
import ClientPortal from "@/pages/operaciones/ClientPortal";
import PrevDashboard from "@/pages/prevencion/PrevDashboard";
import Inspections from "@/pages/prevencion/Inspections";
import Templates from "@/pages/prevencion/Templates";
import Talks from "@/pages/prevencion/Talks";
import Findings from "@/pages/prevencion/Findings";
import Incidents from "@/pages/prevencion/Incidents";
import IsoDocs from "@/pages/iso/IsoDocs";
import IsoDocDetail from "@/pages/iso/IsoDocDetail";
import MyDocs from "@/pages/iso/MyDocs";
import Executive from "@/pages/gerencia/Executive";
import Profitability from "@/pages/gerencia/Profitability";
import ProfitDetail from "@/pages/gerencia/ProfitDetail";
import ComprasDashboard from "@/pages/compras/ComprasDashboard";
import Requests from "@/pages/compras/Requests";
import RequestDetail from "@/pages/compras/RequestDetail";
import Orders from "@/pages/compras/Orders";
import Suppliers from "@/pages/compras/Suppliers";
import Vehicles from "@/pages/flota/Vehicles";
import VehicleDetail from "@/pages/flota/VehicleDetail";

const Perm = ({ perm, children }) => {
  const { can } = useAuth();
  return !perm || can(perm) ? children : <Forbidden />;
};

const ADMIN_ROUTES = [
  ["admin/usuarios", "admin.usuarios.ver", <Users />],
  ["admin/usuarios/:id", "admin.usuarios.ver", <UserDetail />],
  ["admin/roles", "admin.roles.ver", <Roles />],
  ["admin/auditoria", "admin.auditoria.ver", <Audit />],
  ["admin/sesiones", "admin.sesiones.ver", <Sessions />],
  ["admin/configuracion", "admin.configuracion.ver", <Settings />],
  ["admin/maestros", "admin.maestros.ver", <Catalogs />],
  ["admin/respaldos", "admin.respaldos.gestionar", <Backups />],
  ["gerencia", "gerencia.dashboard.ver", <GerenciaDashboard />],
  ["gerencia/ejecutivo", "gerencia.dashboard.ver", <Executive />],
  ["gerencia/rentabilidad", "gerencia.rentabilidad.ver", <Profitability />],
  ["gerencia/rentabilidad/:id", "gerencia.rentabilidad.ver", <ProfitDetail />],
  ["gerencia/clientes", "gerencia.ventas.ver", <Clients />],
  ["gerencia/clientes/:id", "gerencia.ventas.ver", <ClientDetail />],
  ["gerencia/ventas", "gerencia.ventas.ver", <Sales />],
  ["gerencia/ventas/:id", "gerencia.ventas.ver", <SaleDetail />],
  ["gerencia/ot", "gerencia.ot.ver", <Ots />],
  ["gerencia/ot/nueva", "gerencia.ot.crear", <OtForm />],
  ["gerencia/ot/:id", "gerencia.ot.ver", <OtDetail />],
  ["gerencia/ot/:id/editar", "gerencia.ot.editar", <OtForm />],
  ["gerencia/centros-costo", "gerencia.ot.ver", <CostCenters />],
  ["rrhh/empleados", "rrhh.empleados.ver", <Employees />],
  ["rrhh/empleados/:id", "rrhh.empleados.ver", <EmployeeDetail />],
  ["rrhh/tipos-documento", "rrhh.empleados.ver", <DocTypes />],
  ["rrhh/control-documental", "rrhh.control_doc.ver", <ControlDoc />],
  ["rrhh/contratos", "rrhh.contratos.ver", <Contracts />],
  ["rrhh/plantillas-contrato", "rrhh.contratos.ver", <ContractTemplates />],
  ["rrhh/asistencia", "rrhh.asistencia.ver", <Attendance />],
  ["rrhh/escanear", "rrhh.asistencia.registrar", <Scan />],
  ["abastecimiento", "abastecimiento.dashboard.ver", <AbDashboard />],
  ["abastecimiento/bodegas", "abastecimiento.inventario.ver", <Warehouses />],
  ["abastecimiento/bodegas/:id", "abastecimiento.inventario.ver", <WarehouseDetail />],
  ["abastecimiento/productos", "abastecimiento.inventario.ver", <Products />],
  ["abastecimiento/transferencias", "abastecimiento.inventario.ver", <Transfers />],
  ["abastecimiento/movimientos", "abastecimiento.inventario.ver", <Movements />],
  ["abastecimiento/herramientas", "abastecimiento.inventario.ver", <Tools />],
  ["abastecimiento/entregas", "abastecimiento.escanear", <ToolDesk />],
  ["abastecimiento/inventarios", "abastecimiento.inventario.ver", <Counts />],
  ["abastecimiento/inventarios/:id", "abastecimiento.inventario.ver", <CountDetail />],
  ["operaciones", "operaciones.dashboard_ot.ver", <OpsBoard />],
  ["operaciones/ot/:id", "operaciones.dashboard_ot.ver", <OpsOt />],
  ["rrhh/asistencia-dia", "rrhh.asistencia.ver", <DayBoard />],
  ["rrhh/pre-nomina", "rrhh.planilla.ver", <Payroll />],
  ["rrhh/pre-nomina/:id", "rrhh.planilla.ver", <PayrollDetail />],
  ["prevencion", "prevencion.dashboard.ver", <PrevDashboard />],
  ["prevencion/inspecciones", "prevencion.dashboard.ver", <Inspections />],
  ["prevencion/checklists", "prevencion.dashboard.ver", <Templates />],
  ["prevencion/charlas", "prevencion.dashboard.ver", <Talks />],
  ["prevencion/hallazgos", "prevencion.dashboard.ver", <Findings />],
  ["prevencion/incidentes", "prevencion.dashboard.ver", <Incidents />],
  ["compras", "compras.ver", <ComprasDashboard />],
  ["compras/solicitudes", "compras.ver", <Requests />],
  ["compras/solicitudes/:id", "compras.ver", <RequestDetail />],
  ["compras/ordenes", "compras.ver", <Orders />],
  ["compras/proveedores", "compras.ver", <Suppliers />],
  ["flota", "flota.vehiculos.ver", <Vehicles />],
  ["flota/:id", "flota.vehiculos.ver", <VehicleDetail />],
  ["iso", "iso.documentos.ver", <IsoDocs />],
  ["iso/:id", "iso.documentos.ver", <IsoDocDetail />],
  ["iso/mis-documentos", null, <MyDocs />],
];

export default function App() {
  const [installed, setInstalled] = useState(null);
  useEffect(() => { api.get("/install/status").then((r) => setInstalled(r.data.installed)).catch(() => setInstalled(true)); }, []);
  if (installed === null) return null;

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          {!installed && <Route path="*" element={<Install onInstalled={() => setInstalled(true)} />} />}
          {installed && (
            <>
              <Route path="/instalar" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/recuperar" element={<ForgotPassword />} />
              <Route path="/restablecer" element={<ResetPassword />} />
              <Route path="/cliente/ot/:token" element={<ClientPortal />} />
              {TerrenoRoutes}
              <Route element={<AppLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="mi-cuenta" element={<MyAccount />} />
                <Route path="notificaciones" element={<Notifications />} />
                <Route path="mensajes" element={<Messages />} />
                <Route path="soporte" element={<Tickets />} />
                <Route path="soporte/:id" element={<TicketDetail />} />
                <Route path="arquitectura" element={<Perm perm="admin.documentacion_tecnica.ver"><Architecture /></Perm>} />
                {ADMIN_ROUTES.map(([path, perm, el]) => <Route key={path} path={path} element={<Perm perm={perm}>{el}</Perm>} />)}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </>
          )}
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
